// Stencil
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  Watch,
  Listen
} from "@stencil/core";
// Other Libraries
import {
  ComboBoxModel,
  TabularGridSelectionChangedEvent,
  SegmentedControlModel
} from "@genexus/chameleon-controls-library";
import { MercuryBundles, getIconPath } from "@genexus/mercury";
// Custom Imports
import { TopStateBarType } from "../../../components";
import {
  ModuleServerData,
  ModuleData,
  ModuleActionType,
  ModuleFilterType,
  ModuleVersionData
} from "../types";
import {
  mapModuleServerDataToComboBoxModel,
  mapModuleVersionDataToComboBoxModel,
  renderModuleDataProperties
} from "./helpers";
import { config } from "../../../common/config";
import { Locale } from "../../../common/locale";
import { ContextMenuInfo } from "../../../common/types";
import { hiChar } from "../../../common/helpers";
import { ComboBoxItemValue } from "@genexus/gemini";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form",
  "utils/layout",
  "utils/typography",
  "components/button",
  "components/icon",
  "components/combo-box",
  "components/edit",
  "components/tabular-grid",
  "components/segmented-control",
  "chameleon/scrollbar",
  "utils/spacing"
];

// icons
const SHOW_MORE_ICON = getIconPath({
  category: "gemini-tools",
  name: "show-more-horizontal",
  colorType: "primary"
});
const RESET_ICON = getIconPath({
  category: "gemini-tools",
  name: "reset",
  colorType: "primary"
});
const MODULE_ICON = getIconPath({
  category: "objects",
  name: "module"
});
const FILTER_ICON = getIconPath({
  category: "system",
  name: "filters",
  colorType: "on-surface"
});
const MVS = "__"; // The separator between module-id, and module-version-id

@Component({
  tag: "gx-ide-manage-module-references",
  styleUrl: "manage-module-references.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/manage-module-references"]
})
export class GxManageModuleReferences {
  #componentLocale: any;

  /**
   * #moduleVersionsMap maps a module version (identified as module-id + version-id), with the ModuleVersionData
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #moduleVersionsMap: ModulesVersionsMap = new Map();

  /**
   * maps a module-id, with the module current installed version, and the list of versions as a ComboBoxModel representation.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #modulesMap: ModulesMap = new Map();

  // #modulesJustFetched is used to select the current module on componentDidRender
  #modulesJustFetched: boolean = false;
  #searchModulesFilterValue: string;
  #modulesGrid: HTMLChTabularGridElement;

  @Element() el: HTMLGxIdeManageModuleReferencesElement;
  #progressBarEl: HTMLGxIdeProgressBarElement;

  @State() actionInProgress = false;
  @State() executeActionCallbackInProgress = false;
  @State() filteredModules: ModuleData[] = [];
  @State() loadingServer = false;

  @State() modulesAll: ModuleData[] = [];
  @Watch("modulesAll")
  modulesAllChanged(newModules: ModuleData[]) {
    let currentSelectedVersion: string;
    if (this.selectedModuleId) {
      // If this.selectedModuleId has a value, serverSelectedCallback was called
      // but the server is the same. This occurs when a user action (e.g., install
      // or restore) requires modules to update. The selected module and version
      // remain unchanged. We must save the current version before #clearAll().
      currentSelectedVersion = this.#modulesMap.get(
        this.selectedModuleId
      ).currentSelectedVersion;
    }

    this.#clearAll();
    newModules.forEach(module => {
      let currentSelectedVersionValue = module.versions[0].id;
      if (currentSelectedVersion && this.selectedModuleId === module.id) {
        currentSelectedVersionValue = currentSelectedVersion;
        currentSelectedVersion === undefined; // found. prevent the if in the following iteration.
      }

      // fulfill #modulesMap
      const moduleVersionsComboBoxModel = mapModuleVersionDataToComboBoxModel(
        module.versions
      );
      this.#modulesMap.set(module.id, {
        moduleData: module,
        currentInstalledVersion: module.currentVersion,
        currentSelectedVersion: currentSelectedVersionValue,
        comboBoxVersionsModel: moduleVersionsComboBoxModel
      });

      // fulfill #moduleVersionsMap.
      module.versions.forEach(version => {
        const moduleVersionCombinedId = `${module.id}${MVS}${version.id}`;
        this.#moduleVersionsMap.set(moduleVersionCombinedId, {
          id: version.id,
          action: version.action,
          description: version.description,
          properties: version.properties,
          name: version.name,
          warningMessage: version.warningMessage
        });
      });
    });

    this.#modulesJustFetched = true;
    this.#filterModules();
  }

  @State() progressState: TopStateBarType = "in-progress";
  @State() progressValue: number = 0;
  @State() reRenderCounter: number = 0;
  @State() selectedModuleAction: ActionInfo = {
    message: "",
    progress: 0,
    state: "in-progress"
  };
  @State() selectedModuleId: string;
  @State() selectedModuleType: ModuleFilterType = "browse";
  @State() selectedServerId: string;
  @State() selectedServerValue: string;
  // serversComboBoxModel is a model representation of the servers ModuleServerData[]
  @State() serversComboBoxModel: ComboBoxModel = [];
  @State() selectedModuleVersions: ComboBoxModel = [];
  #segmentedControlModel: SegmentedControlModel = [];

  /**
   * If true, the progress bar will close automatically once the progress has reached 100%.
   */
  @Prop() readonly autoCloseProgressBar: boolean = false;

  /**
   * Callback invoked when the user wants to execute the action corresponding to a selected module. When the action begins to be executed, the component must be able to show the progress of the action. For this, the onProgress parameter is available, which is in turn a callback used by the host to notify the progress by text message and percentage.
   */
  @Prop() readonly executeActionCallback!: ExecuteActionCallback;

  /**
   * Callback invoked when an element is right clicked on servers list
   */
  @Prop() readonly serverContextMenuCallback: ServerContextMenuCallback;

  /**
   * Callback invoked when a server is selected. Returns the set of modules available on that server.
   */
  @Prop() readonly serverSelectedCallback: ServerSelectedCallback;

  /**
   * List of module servers currently cataloged
   */
  @Prop() readonly servers: ModuleServerData[] = [];
  @Watch("servers")
  serversChanged(newServers: ModuleServerData[]) {
    if (newServers.length) {
      this.serversComboBoxModel =
        mapModuleServerDataToComboBoxModel(newServers);
      this.selectedServerValue = this.serversComboBoxModel[0].value;
    }
  }

  /**
   * Allows you to hide the button (...) that executes serverContextMenuCallback
   */
  @Prop() readonly showServerCommands: boolean = false;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.serversChanged(this.servers); // map ModuleServerData to ComboBoxModel

    this.#segmentedControlModel = [
      {
        id: "browse",
        caption: this.#componentLocale.modules.buttonsContainer.browse
      },
      {
        id: "installed",
        caption: this.#componentLocale.modules.buttonsContainer.installed
      },
      {
        id: "updates",
        caption: this.#componentLocale.modules.buttonsContainer.updates
      }
    ];
  }

  async componentDidLoad() {
    await this.#serverSelectedCallbackHandler(this.servers[0].id, false);
  }

  componentDidRender() {
    if (this.#modulesJustFetched && this.selectedModuleId) {
      // set selected module as selected on the grid after server reload.
      this.#modulesGrid.selectRow(this.selectedModuleId, true);
      this.#modulesJustFetched = false;
    }
  }

  @Listen("progressBarClosed")
  progressBarClosedHandler() {
    // set it to false, to fire a re-render the next time the progress-bar is active.
    this.actionInProgress = false;
  }

  #clearAll = () => {
    this.#modulesMap.clear();
    this.#moduleVersionsMap.clear();
  };

  #filterModules = () => {
    let filteredModules;
    // 1. Filter by type first
    // browse
    if (this.selectedModuleType === "browse") {
      filteredModules = this.modulesAll;
    }
    // installed
    else if (this.selectedModuleType === "installed") {
      filteredModules = this.modulesAll.filter((module: ModuleData) => {
        // if the module has a currentVersion (not undefined) it means it is installed
        return module.currentVersion;
      });
    } else if (this.selectedModuleType === "updates") {
      filteredModules = this.modulesAll.filter((module: ModuleData) => {
        return module.versions.find(version => version.action === "update");
      });
    }

    // 2. Then filter by user input
    if (this.#searchModulesFilterValue) {
      filteredModules = filteredModules.filter(module => {
        const moduleSelectedVersion = this.#moduleVersionsMap.get(
          `${module.id}${MVS}${
            this.#modulesMap.get(module.id).currentSelectedVersion
          }`
        );
        const moduleSelectedVersionName =
          moduleSelectedVersion.name.toLowerCase();
        const moduleSelectedVersionDescription =
          moduleSelectedVersion.description.toLowerCase();

        return (
          moduleSelectedVersionName?.includes(this.#searchModulesFilterValue) ||
          moduleSelectedVersionDescription?.includes(
            this.#searchModulesFilterValue
          )
        );
      });
    }

    this.filteredModules = filteredModules;
  };

  #getActionFromCurrentModuleVersion = (): ModuleActionType => {
    const action = this.#moduleVersionsMap.get(
      `${this.selectedModuleId}${MVS}${
        this.#modulesMap.get(this.selectedModuleId).currentSelectedVersion
      }`
    ).action;
    return action || "install";
  };

  #handleFilterChangeHandler = (event: CustomEvent<string> | InputEvent) => {
    const searchValue = (event.detail as string).trim().toLowerCase();
    this.#searchModulesFilterValue = searchValue;
    this.#filterModules();
  };

  #handleModuleChange = (
    event: CustomEvent<TabularGridSelectionChangedEvent>
  ) => {
    this.selectedModuleId = event.detail.rowsId[0];

    // update selected versions comboBox model
    const selectedModuleVersionsInfoArray = this.#modulesMap.get(
      this.selectedModuleId
    ).comboBoxVersionsModel;

    this.selectedModuleVersions = selectedModuleVersionsInfoArray.map(
      versionInfo => {
        return {
          caption: versionInfo.value,
          value: versionInfo.value
        } as ComboBoxItemValue;
      }
    );
  };

  /**
   * Perform an action relative to a module using callbackExecuteAction callback and receive the state of that action with onProgress callback
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #moduleActionHandler = (module: ModuleData) => async () => {
    await this.#progressBarEl.reset(); // sets the progress bar to 0
    this.executeActionCallbackInProgress = true;
    this.actionInProgress = true;

    const moduleSelectedVersionId = this.#modulesMap.get(
      this.selectedModuleId
    ).currentSelectedVersion;

    const actionSuccessful = await this.executeActionCallback(
      this.selectedServerId,
      module.id,
      moduleSelectedVersionId,
      this.#getActionFromCurrentModuleVersion(),
      this.#onProgress
    );
    this.executeActionCallbackInProgress = false;
    if (actionSuccessful) {
      this.#serverSelectedCallbackHandler(this.selectedServerId, false);
    }
  };

  #moduleTypeButtonClicked = (event: CustomEvent<string>): void => {
    this.selectedModuleType = event.detail as ModuleFilterType;
    this.#filterModules();
  };

  #moduleVersionsValueChangedHandler = (
    event: CustomEvent<string> | InputEvent
  ) => {
    const versionId = event.detail;
    const currentModuleMapValue = this.#modulesMap.get(this.selectedModuleId);
    currentModuleMapValue.currentSelectedVersion = versionId.toString();
    this.#modulesMap.set(this.selectedModuleId, currentModuleMapValue);
    this.reRenderCounter++;
  };

  /**
   * Callback invoked to get the progress when module is performing an action
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #onProgress = (
    message: string,
    progress: number,
    state: TopStateBarType
  ): void => {
    const data = { message, progress, state };
    this.selectedModuleAction = { ...data };
    this.progressValue = this.selectedModuleAction.progress;
    this.progressState = this.selectedModuleAction.state;
  };

  #progressBarCloseHandler = () => {};

  #reloadServer = () => {
    this.#serverSelectedCallbackHandler(this.selectedServerId, true);
  };

  #renderEmptyStateModules = (): HTMLGxIdeEmptyStateElement => {
    return (
      <gx-ide-empty-state
        isAnimated={true}
        stateIconSrc={FILTER_ICON}
        stateTitle={this.#componentLocale.modules.noModulesFilter}
      ></gx-ide-empty-state>
    );
  };

  #renderInstalledVersion = () => {
    const currentInstalledVersion = this.#modulesMap.get(
      this.selectedModuleId
    ).currentInstalledVersion;
    let caption;
    let cssClass = "module-info__action-container";
    if (currentInstalledVersion) {
      caption = `${
        this.#componentLocale.modulesInformation.moduleProperties.installed
      }: ${currentInstalledVersion}`;
      cssClass += ` module-info__action-container--installed`;
    } else {
      caption =
        this.#componentLocale.modulesInformation.moduleProperties.notInstalled;
    }
    return (
      <div class={cssClass}>
        <p class="body-regular-m">{caption}</p>
      </div>
    );
  };

  #renderModuleVersions = () => {
    const currentSelectedModule = this.#modulesMap.get(this.selectedModuleId);
    const selectedVersionValue = currentSelectedModule.currentSelectedVersion;
    const moduleSelectedVersion = this.#moduleVersionsMap.get(
      `${this.selectedModuleId}${MVS}${
        this.#modulesMap.get(this.selectedModuleId).currentSelectedVersion
      }`
    );
    const moduleSelectedVersionAction = moduleSelectedVersion.action;

    return (
      <div class="field field-block available-versions">
        <label class="label" htmlFor="available-versions">
          {this.#componentLocale.availableVersions}
        </label>
        <div class="buttons-spacer combo-and-button">
          <ch-combo-box-render
            id="available-versions"
            class="combo-box versions"
            value={selectedVersionValue}
            onInput={this.#moduleVersionsValueChangedHandler}
            disabled={this.loadingServer}
            model={this.selectedModuleVersions}
          ></ch-combo-box-render>
          <button
            class="button-primary button-status"
            disabled={
              this.executeActionCallbackInProgress ||
              moduleSelectedVersionAction === "incompatible" ||
              moduleSelectedVersionAction === "not-installable"
            }
            onClick={this.#moduleActionHandler(
              currentSelectedModule.moduleData
            )}
          >
            {this.#getActionFromCurrentModuleVersion()}
          </button>
        </div>
      </div>
    );
  };

  #renderModulesGrid = (): Element => {
    return (
      <ch-tabular-grid
        // TODO: Evaluate if the tabular-grid is the right tool for this job.
        rowSelectionMode="single"
        part="ch-grid-pending-commits"
        class="tabular-grid scrollable"
        onSelectionChanged={this.#handleModuleChange}
        ref={el => (this.#modulesGrid = el as HTMLChTabularGridElement)}
      >
        <ch-tabular-grid-columnset class="tabular-grid-column-set">
          <ch-tabular-grid-column
            class="tabular-grid-column"
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.common}
          ></ch-tabular-grid-column>
        </ch-tabular-grid-columnset>

        {this.filteredModules.length ? (
          <ch-tabular-grid-rowset class="tabular-grid-rowset">
            {this.filteredModules.map((module: ModuleData) => {
              const moduleIsInstalled = module.currentVersion;

              const moduleSelectedVersion = this.#moduleVersionsMap.get(
                `${module.id}${MVS}${
                  this.#modulesMap.get(module.id).currentSelectedVersion
                }`
              );

              const moduleSelectedVersionName = moduleSelectedVersion.name;
              const moduleSelectedVersionDescription =
                moduleSelectedVersion.description;

              return (
                <ch-tabular-grid-row
                  // TODO: Replace tabular grid with other control, since it does not makes sense to use a grid with only one column.
                  class="tabular-grid-row"
                  rowid={module.id}
                  key={module.id}
                >
                  <ch-tabular-grid-cell class="tabular-grid-cell">
                    <div class="module-grid-item-info field-group">
                      <ch-image
                        class="icon-m module-icon"
                        src={MODULE_ICON}
                        disabled={!moduleIsInstalled}
                      ></ch-image>
                      <div class="field field-block">
                        <h6 class="subtitle-semi-bold-xs">
                          {hiChar(
                            moduleSelectedVersionName,
                            this.#searchModulesFilterValue
                          )}
                        </h6>

                        <p class="body-regular-s">
                          {hiChar(
                            moduleSelectedVersionDescription,
                            this.#searchModulesFilterValue
                          )}
                        </p>
                      </div>
                    </div>
                  </ch-tabular-grid-cell>
                </ch-tabular-grid-row>
              );
            })}
          </ch-tabular-grid-rowset>
        ) : (
          <ch-tabular-grid-rowset class="tabular-grid-rowset">
            <ch-tabular-grid-rowset-empty>
              {this.#renderEmptyStateModules()}
            </ch-tabular-grid-rowset-empty>
          </ch-tabular-grid-rowset>
        )}
      </ch-tabular-grid>
    );
  };

  /* module details/information renders*/
  #renderVersionName = (): HTMLElement => {
    const moduleSelectedVersion = this.#moduleVersionsMap.get(
      `${this.selectedModuleId}${MVS}${
        this.#modulesMap.get(this.selectedModuleId).currentSelectedVersion
      }`
    );

    const selectedModule = this.#modulesMap.get(this.selectedModuleId);
    const moduleIsInstalled = selectedModule.currentInstalledVersion;

    return (
      <div class="module-info__title">
        <ch-image
          class="icon-l module-info__icon"
          src={MODULE_ICON}
          disabled={!moduleIsInstalled}
        ></ch-image>
        <h6 class="subtitle-semi-bold-s">{moduleSelectedVersion.name}</h6>
      </div>
    );
  };

  #renderVersionWarningMessage = () => {
    const moduleVersionWarningMessage = this.#moduleVersionsMap.get(
      `${this.selectedModuleId}${MVS}${
        this.#modulesMap.get(this.selectedModuleId).currentSelectedVersion
      }`
    ).warningMessage;

    if (moduleVersionWarningMessage) {
      return (
        <p class="module-info__alert-message body-regular-s">
          {moduleVersionWarningMessage}
        </p>
      );
    }
  };

  #serverSelectedCallbackHandler = async (id: any, reload: boolean) => {
    if (this.serverSelectedCallback) {
      this.selectedServerId = id;
      this.loadingServer = true;
      const resolvedObjects = await this.serverSelectedCallback(id, reload);
      this.modulesAll = [...resolvedObjects];
      this.loadingServer = false;
    }
  };

  #serverValueChangedHandler = (event: CustomEvent<string> | InputEvent) => {
    this.selectedModuleId = undefined; // clear selected module
    this.#searchModulesFilterValue = ""; // clear filter
    this.selectedModuleType = "browse"; // set "browse" as the default selected type
    const serverId = event.detail;
    this.#serverSelectedCallbackHandler(serverId, false);
  };

  #showModuleMenu = async (e: MouseEvent) => {
    if (this.serverContextMenuCallback) {
      const buttonBoundingClientRect = (
        e.target as HTMLElement
      ).getBoundingClientRect();
      await this.serverContextMenuCallback({
        selection: [this.selectedServerId],
        clientX: buttonBoundingClientRect.x,
        clientY: buttonBoundingClientRect.y
      });
    }
  };

  render(): void {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="main">
          <div class="modules__container field-group">
            <header class="modules__header field-group">
              <div class="field field-block">
                <label class="label" htmlFor="servers-list">
                  {this.#componentLocale.modules.selectServer}
                </label>
                <div class="modules__header-controls buttons-spacer">
                  <ch-combo-box-render
                    // select server
                    id="servers-list"
                    class="combo-box combo-box-servers"
                    model={this.serversComboBoxModel}
                    value={this.selectedServerValue}
                    disabled={this.loadingServer}
                    onInput={this.#serverValueChangedHandler}
                  ></ch-combo-box-render>

                  <div
                  // action buttons
                  >
                    {this.showServerCommands && (
                      <button
                        // server context menu button
                        class="button-tertiary button-icon-only"
                        onClick={this.#showModuleMenu}
                        aria-label={this.#componentLocale.showServerMenu}
                        title={this.#componentLocale.showServerMenu}
                      >
                        <ch-image
                          src={SHOW_MORE_ICON}
                          class="icon-m"
                        ></ch-image>
                      </button>
                    )}

                    <button
                      // reload server selected button
                      class="button-tertiary button-icon-only"
                      onClick={this.#reloadServer}
                      aria-label={this.#componentLocale.reloadModules}
                      title={this.#componentLocale.reloadModules}
                    >
                      <ch-image src={RESET_ICON} class="icon-m"></ch-image>
                    </button>
                  </div>
                </div>
              </div>
            </header>

            <div
              // Modules main
              class="modules__main"
            >
              <div class="modules__main-header">
                <div
                  // search modules
                  class="field field-block"
                >
                  <label class="label" htmlFor="search-modules">
                    {this.#componentLocale.modules.searchModules}
                  </label>
                  <ch-edit
                    class="input"
                    value={this.#searchModulesFilterValue}
                    onInput={this.#handleFilterChangeHandler}
                    disabled={this.loadingServer}
                  ></ch-edit>
                </div>

                <div class="browse-modules">
                  <ch-segmented-control-render
                    class="segmented-control-filled"
                    model={this.#segmentedControlModel}
                    selectedId={this.selectedModuleType}
                    onSelectedItemChange={this.#moduleTypeButtonClicked}
                  ></ch-segmented-control-render>
                </div>
              </div>

              <div class="grid-or-loader-container">
                {this.loadingServer ? (
                  <gx-ide-loader
                    loaderTitle={this.#componentLocale.servers.loading}
                    show={true}
                  ></gx-ide-loader>
                ) : (
                  this.#renderModulesGrid()
                )}
              </div>
            </div>
          </div>

          <div class="module-info field-group">
            {this.selectedModuleId ? (
              [
                <header class="module-info__header">
                  {this.#renderVersionName()}
                  {this.#renderInstalledVersion()}
                  {this.#renderModuleVersions()}
                  {this.#renderVersionWarningMessage()}
                </header>,
                <div class="module-info__properties scrollable">
                  <div class="module-info__properties-inner-wrapper">
                    {renderModuleDataProperties(
                      this.#moduleVersionsMap.get(
                        `${this.selectedModuleId}__${
                          this.#modulesMap.get(this.selectedModuleId)
                            .currentSelectedVersion
                        }`
                      ).properties
                    )}
                  </div>
                </div>
              ]
            ) : (
              <gx-ide-empty-state
                class="recent-objects__empty-state"
                stateIconSrc={MODULE_ICON}
                stateTitle={
                  this.#componentLocale.modulesInformation.emptyState.title
                }
                stateDescription={
                  this.#componentLocale.modulesInformation.emptyState
                    .description
                }
              ></gx-ide-empty-state>
            )}
          </div>

          <footer class="footer">
            <gx-ide-progress-bar
              class="border-block-start"
              slot="footer-above"
              part="progress-bar"
              stateType={this.progressState}
              caption={
                this.selectedModuleAction.message ||
                this.#componentLocale.progressBar.defaultCaption
              }
              progress={this.progressValue}
              active={this.actionInProgress}
              closeType={this.autoCloseProgressBar ? "auto-close" : "button"}
              closedCallback={this.#progressBarCloseHandler}
              ref={el =>
                (this.#progressBarEl = el as HTMLGxIdeProgressBarElement)
              }
            ></gx-ide-progress-bar>
          </footer>
        </section>
      </Host>
    );
  }
}

export type ExecuteActionCallback = (
  serverId: string,
  moduleId: string,
  moduleVersionId: string,
  action: ModuleActionType,
  onProgress: (
    message: string,
    progress: number,
    state: TopStateBarType
  ) => void
) => Promise<boolean>;

export type ServerSelectedCallback = (
  id: string,
  reload: boolean
) => Promise<ModuleData[]>;

export type ServerContextMenuCallback = (
  contextMenuInfo: ContextMenuInfo
) => Promise<void>;
export interface ActionInfo {
  message: string;
  progress: number;
  state: TopStateBarType;
}

export type ModuleVersionsMap = Map<string, { value: string; caption: string }>;
export type ModulesMap = Map<string, ModuleMapValue>;
export type ModuleMapValue = {
  moduleData: ModuleData;
  currentInstalledVersion: string;
  currentSelectedVersion: string;
  comboBoxVersionsModel: ComboBoxModel;
};
export type ModulesVersionsMap = Map<string, ModuleVersionData>;
